/* Pivo Arena — Concept 01. GSAP scroll narrative with graceful fallbacks. */
(function () {
  const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const hasGsap = typeof gsap !== "undefined" && typeof ScrollTrigger !== "undefined";
  const clamp = (v, a, b) => Math.max(a, Math.min(b, v));

  /* ---- reveal-on-enter for all major text blocks ---- */
  // The recall bar is fixed and starts off-screen, so a reveal on it would never
  // fire — keep its contents out of the reveal set.
  const revealables = [...document.querySelectorAll(
    ".kicker, .h-display, .lede, .feature-index li, .know-block, .metric, .device, .plan, .close-line, .team-card, .hero-foot, .badges, .pills li, .ps-note, .trust-grid > div, .promo-copy, .recap-hero, .recap-stats"
  )].filter((el) => !el.closest("#recall"));
  revealables.forEach((el) => el.classList.add("rv"));
  const io = new IntersectionObserver(
    (entries) => entries.forEach((e) => {
      if (e.isIntersecting) { e.target.classList.add("in"); io.unobserve(e.target); }
    }),
    // positive bottom margin fires the reveal just before the block scrolls in,
    // so content is already on screen rather than fading in after you arrive
    { threshold: 0, rootMargin: "0px 0px 14% 0px" }
  );
  revealables.forEach((el) => io.observe(el));

  // Belt and braces: IO can miss programmatic jumps and some embedded contexts.
  // A throttled manual sweep guarantees nothing stays invisible once on screen.
  let sweepQueued = false;
  function sweep() {
    sweepQueued = false;
    const vh = window.innerHeight;
    revealables.forEach((el) => {
      if (el.classList.contains("in")) return;
      const b = el.getBoundingClientRect();
      if (b.top < vh * 1.15 && b.bottom > -vh) el.classList.add("in");
    });
  }
  function queueSweep() {
    if (!sweepQueued) { sweepQueued = true; requestAnimationFrame(sweep); }
  }
  addEventListener("scroll", queueSweep, { passive: true });
  addEventListener("resize", queueSweep, { passive: true });
  setTimeout(sweep, 300);
  setTimeout(sweep, 1200);

  // A backgrounded tab suspends IntersectionObserver and CSS transitions, so a
  // reader who switches away mid-page can come back to blank sections. Re-sweep
  // (and repaint the scroll-driven sections) whenever the page becomes visible.
  addEventListener("visibilitychange", () => {
    if (!document.hidden) {
      sweep();
      dispatchEvent(new Event("scroll"));
    }
  });

  /* ---- chapter counter + progress bar ---- */
  const num = document.getElementById("chapter-num");
  const name = document.getElementById("chapter-name");
  const total = document.getElementById("chapter-total");
  const bar = document.getElementById("progress-bar");
  const chapters = [...document.querySelectorAll("[data-chapter]")];
  const chapterIds = [...new Set(chapters.map((c) => c.dataset.chapter))];
  if (total) total.textContent = String(chapterIds.length).padStart(2, "0");
  function updateChapter() {
    const probe = window.scrollY + window.innerHeight * 0.45;
    let active = chapters[0];
    for (const c of chapters) if (c.offsetTop <= probe) active = c;
    if (num.textContent !== active.dataset.chapter) {
      num.textContent = active.dataset.chapter;
      name.textContent = active.dataset.name;
    }
    const h = document.documentElement;
    bar.style.width = (h.scrollTop / (h.scrollHeight - h.clientHeight)) * 100 + "%";
  }
  addEventListener("scroll", updateChapter, { passive: true });
  updateChapter();

  /* ---- header + hero copy return on scroll-up (board note 1.3) ---- */
  const chrome = document.getElementById("chrome");
  const recall = document.getElementById("recall");
  const hero = document.querySelector(".hero");
  // the closing section has its own store buttons, and on the store the sign-up band and footer
  // follow it: once it is on screen the bar stays away so it never covers the Email field
  const closing = document.querySelector(".close");
  let lastY = window.scrollY;
  function scrollDirection() {
    const y = window.scrollY;
    const dy = y - lastY;
    if (Math.abs(dy) < 6) return;
    const pastHero = hero ? y > hero.offsetHeight * 0.8 : y > 600;
    const atEnd = closing ? closing.getBoundingClientRect().top < window.innerHeight : false;
    const up = dy < 0;
    if (chrome) chrome.classList.toggle("is-hidden", !up && pastHero);
    if (recall) {
      // the bar's store links are only reachable (tab, screen reader) while the bar is showing
      const on = up && pastHero && !atEnd;
      recall.classList.toggle("is-on", on);
      recall.inert = !on;
      recall.setAttribute("aria-hidden", on ? "false" : "true");
    }
    lastY = y;
  }
  addEventListener("scroll", scrollDirection, { passive: true });

  /* ---- whichever element's centre sits closest to the viewport centre ---- */
  function nearestToCentre(list) {
    const mid = window.innerHeight / 2;
    let best = null, bestD = Infinity;
    list.forEach((el) => {
      const r = el.getBoundingClientRect();
      const d = Math.abs(r.top + r.height / 2 - mid);
      if (d < bestD) { bestD = d; best = el; }
    });
    return best;
  }

  /* ---- 03 · Know Your Arena: phone screen swaps per point ---- */
  const screens = [...document.querySelectorAll(".ph-screen")];
  const knowBlocks = [...document.querySelectorAll(".know-block")];
  if (knowBlocks.length && screens.length) {
    function paintKnow() {
      const active = nearestToCentre(knowBlocks);
      if (!active) return;
      const want = active.dataset.screen;
      screens.forEach((s) => {
        const on = s.dataset.step === want;
        if (s.classList.contains("is-on") === on) return;
        s.classList.toggle("is-on", on);
        if (s.play) { on ? s.play().catch(() => {}) : s.pause(); }
      });
    }
    addEventListener("scroll", paintKnow, { passive: true });
    addEventListener("resize", paintKnow, { passive: true });
    paintKnow();
  }

  /* ---- 03 · the same three screens as a swipe carousel on phones ----
     Bella (4 Sep): "only the first one is seen ... the viewer can never see the other
     two". Above 900px this is inert: CSS hides the dots and the scroll-swap above
     still runs. Below it, CSS lays the screens out side by side and this only wires
     the dots, so the `is-on` toggling above stays harmless (opacity is forced to 1). */
  const rail = document.querySelector(".phone");
  const dots = document.querySelector(".ph-dots");
  if (rail && dots && screens.length > 1) {
    /* Centre slide i in the rail. Deliberately behavior:"auto": inside a
       scroll-snap-type:x mandatory container a smooth scroll is simply dropped
       (measured: scrollLeft stayed 0 for 1.8s), so smooth would make the dots dead. */
    const goTo = (i) => rail.scrollTo({
      left: screens[i].offsetLeft - (rail.clientWidth - screens[i].offsetWidth) / 2,
      behavior: "auto",
    });
    screens.forEach((s, i) => {
      const b = document.createElement("button");
      b.type = "button";
      b.setAttribute("aria-label", "Show screen " + (i + 1) + " of " + screens.length);
      b.addEventListener("click", () => goTo(i));
      dots.appendChild(b);
    });
    const nearest = () => {
      const mid = rail.scrollLeft + rail.clientWidth / 2;
      let best = 0, bd = Infinity;
      screens.forEach((s, i) => {
        const d = Math.abs(s.offsetLeft + s.offsetWidth / 2 - mid);
        if (d < bd) { bd = d; best = i; }
      });
      return best;
    };
    const paintDots = () => {
      const best = nearest();
      [...dots.children].forEach((d, n) => d.classList.toggle("is-on", n === best));
    };
    rail.addEventListener("scroll", paintDots, { passive: true });
    /* The rail now has half-width spacers at both ends so every slide can centre, which
       means scrollLeft 0 is blank padding: centre the nearest slide at init, again once
       the images have their real widths, and on rotate. No-op above 900px, where .phone
       is not a scroll container. */
    const recentre = () => { goTo(nearest()); paintDots(); };
    addEventListener("resize", recentre, { passive: true });
    addEventListener("load", recentre);
    recentre();
  }

  /* ---- count-ups (map metrics, recap stats, trust stats) ---- */
  function countUp(el) {
    const target = parseFloat(el.dataset.count);
    const dec = parseInt(el.dataset.dec || "0", 10);
    const suffix = el.dataset.suffix || "";
    const t0 = performance.now();
    const dur = 1600;
    (function tick(t) {
      const p = Math.min(1, (t - t0) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      el.textContent = (target * eased).toFixed(dec) + suffix;
      if (p < 1) requestAnimationFrame(tick);
    })(t0);
  }
  const counters = [...document.querySelectorAll("[data-count]")];
  const counted = new WeakSet();
  function fireCount(el) {
    if (counted.has(el)) return;
    counted.add(el);
    countUp(el);
  }
  const countIO = new IntersectionObserver(
    (entries) => entries.forEach((e) => {
      if (e.isIntersecting) { fireCount(e.target); countIO.unobserve(e.target); }
    }),
    { threshold: 0.5 }
  );
  counters.forEach((c) => countIO.observe(c));
  // same belt-and-braces as the reveals: guarantee the numbers run even without IO delivery
  function sweepCounters() {
    const vh = window.innerHeight;
    counters.forEach((el) => {
      if (counted.has(el)) return;
      const b = el.getBoundingClientRect();
      if (b.top < vh * 0.9 && b.bottom > 0) fireCount(el);
    });
  }
  addEventListener("scroll", sweepCounters, { passive: true });
  setTimeout(sweepCounters, 400);

  /* ---- shared helper: how far through a tall pinned section are we (0..1) ---- */
  function sectionProgress(section) {
    const rect = section.getBoundingClientRect();
    /* Use the pinned child's height (100vh = the LARGE viewport), not innerHeight. A phone
       changes innerHeight every time its toolbar slides in or out, which is what happens
       on scroll-up, and every change re-derived the progress and jumped the transforms
       (Bella #25, pose section "very jittery"). The pin does not change with the toolbar. */
    const pin = section.querySelector(".pose-pin, .reel-pin");
    const vh = (pin && pin.offsetHeight) || window.innerHeight;
    const travel = section.offsetHeight - vh;
    if (travel <= 0) return rect.top <= 0 ? 1 : 0;
    return clamp(-rect.top / travel, 0, 1);
  }

  /* ---- 05 · The Record reel: cards + title float up, video cuts to the second one ---- */
  const reel = document.querySelector(".reel");
  if (reel) {
    const heads = [...reel.querySelectorAll(".reel-head")];
    const vids = [...reel.querySelectorAll(".reel-vid")];
    const cardSets = [...reel.querySelectorAll(".reel-cards")];
    const isStacked = () => window.matchMedia("(max-width: 900px)").matches;

    /* Phones show ONE act, chosen by the tabs. The old stacked branch showed both heads
       and both card sets but pinned the video to index 0, so act 2 rendered a heading and
       cards with no visual at all: the second medium lives in the same .reel-frame and
       nothing ever switched it once the section stopped being pinned. */
    const tabs = [...reel.querySelectorAll(".reel-tab")];
    let stage = 0;

    function paintStacked() {
      heads.forEach((h) => {
        h.style.cssText = "";
        h.classList.toggle("is-on", +h.dataset.stage === stage);
      });
      cardSets.forEach((c) => {
        c.style.cssText = "";
        c.classList.toggle("is-on", +c.dataset.stage === stage);
        [...c.children].forEach((card) => { card.style.transform = ""; card.style.opacity = ""; });
        if (+c.dataset.stage === stage) c.scrollLeft = 0;   /* new act starts at card 1 */
      });
      vids.forEach((v) => {
        const on = +v.dataset.stage === stage;
        v.classList.toggle("is-on", on);
        if (v.play) { on ? v.play().catch(() => {}) : v.pause(); }
      });
      tabs.forEach((t) => {
        const on = +t.dataset.stage === stage;
        t.classList.toggle("is-on", on);
        t.setAttribute("aria-selected", on ? "true" : "false");
      });
    }

    tabs.forEach((t) => t.addEventListener("click", () => {
      stage = +t.dataset.stage;
      paintStacked();
    }));

    function paintReel() {
      if (isStacked()) { paintStacked(); return; }
      const p = sectionProgress(reel);
      // Sequenced handover, not a cross-dissolve: stage 0 is fully gone by 0.48 and
      // stage 1 only starts at 0.52, so the two Anton headlines never overlap.
      const out = 1 - clamp((p - 0.40) / 0.08, 0, 1); // 1 → 0 across 0.40–0.48
      const into = clamp((p - 0.52) / 0.08, 0, 1);    // 0 → 1 across 0.52–0.60

      heads.forEach((h) => {
        const first = +h.dataset.stage === 0;
        const on = first ? out : into;
        h.style.opacity = on;
        h.style.transform = "translateY(" + (first ? -46 * (1 - out) : 46 * (1 - into)) + "px)";
        h.classList.toggle("is-on", on > 0.5);
        h.style.pointerEvents = on > 0.5 ? "" : "none";
      });

      cardSets.forEach((set) => {
        const first = +set.dataset.stage === 0;
        const on = first ? out : into;
        set.style.opacity = on;
        set.classList.toggle("is-on", on > 0.5);
        const cards = [...set.children];
        // Each card owns a slice of its stage's scroll, so exactly one is lit at a
        // time and the next takes over as you keep going (Bella, 2 Sep).
        const span = first ? [0.02, 0.40] : [0.60, 0.96];
        const local = clamp((p - span[0]) / (span[1] - span[0]), 0, 1);
        const cursor = local * cards.length;
        cards.forEach((card, i) => {
          const lift = first ? -(60 + i * 22) * (1 - out) : (70 + i * 18) * (1 - into);
          card.style.transform = "translateY(" + lift + "px)";
          const lit = on > 0.5 && cursor >= i && cursor < i + 1;
          card.classList.toggle("is-lit", lit);
          card.classList.toggle("is-seen", on > 0.5 && cursor >= i);
        });
      });

      vids.forEach((v) => {
        const on = +v.dataset.stage === 0 ? p < 0.5 : p >= 0.5;
        v.classList.toggle("is-on", on);
        if (v.play) { on ? v.play().catch(() => {}) : v.pause(); }
      });
    }
    addEventListener("scroll", paintReel, { passive: true });
    addEventListener("resize", paintReel, { passive: true });
    paintReel();
  }

  /* ---- pinned-section progress rail + keep-scrolling cue ----
     Bella (2 Sep): people think they have hit the bottom inside the long pins.
     The rail shows how much of the section is left; the chevron fades near the end. */
  const pinned = [...document.querySelectorAll(".reel, .pose")].map((sec) => ({
    sec,
    fill: sec.querySelector(".pin-rail i"),
    cue: sec.querySelector(".pin-cue"),
  })).filter((x) => x.fill);
  function paintRails() {
    const stacked = window.matchMedia("(max-width: 900px)").matches;
    pinned.forEach(({ sec, fill, cue }) => {
      if (stacked) { fill.style.height = "0%"; return; }
      const p = sectionProgress(sec);
      fill.style.height = (p * 100).toFixed(1) + "%";
      if (cue) cue.style.opacity = (1 - clamp((p - 0.72) / 0.18, 0, 1)).toFixed(2);
    });
  }
  addEventListener("scroll", paintRails, { passive: true });
  addEventListener("resize", paintRails, { passive: true });
  paintRails();

  /* ---- 06 · Pose Analysis: the panel rises and locks mid-banner ---- */
  const pose = document.querySelector(".pose");
  const posePanel = document.getElementById("pose-panel");
  const poseFigure = document.querySelector(".pose-figure");
  const phonePose = () => window.matchMedia("(max-width: 640px)").matches;
  if (pose && posePanel) {
    function paintPose() {
      const p = sectionProgress(pose);
      // Lock early: the payload of this section settles by 40% and stays put rather
      // than arriving as you are leaving.
      const rise = 1 - clamp(p / 0.40, 0, 1);
      if (phonePose() && poseFigure) {
        /* Bella #26: on phones the rider slides in from the left instead of the card
           (the card is display:none there). Same progress curve as the card. */
        poseFigure.style.transform = "translateX(" + (-130 * rise).toFixed(2) + "%)";
        poseFigure.style.opacity = clamp(p / 0.12, 0, 1);
        return;
      }
      if (poseFigure) { poseFigure.style.transform = ""; poseFigure.style.opacity = ""; }
      posePanel.style.transform = "translateY(calc(-50% + " + (rise * 70).toFixed(2) + "vh))";
      posePanel.style.opacity = clamp(p / 0.12, 0, 1);
    }
    /* one paint per frame: phones fire several scroll events per frame while the
       toolbar animates, and each one re-wrote the transform (Bella #25) */
    let poseRaf = 0;
    const queuePose = () => {
      if (poseRaf) return;
      poseRaf = requestAnimationFrame(() => { poseRaf = 0; paintPose(); });
    };
    addEventListener("scroll", queuePose, { passive: true });
    addEventListener("resize", queuePose, { passive: true });
    paintPose();
  }

  /* ---- 07 · Train Together: left photo follows the card carousel, title fades out ---- */
  const teamCards = [...document.querySelectorAll(".team-card")];
  const teamShots = [...document.querySelectorAll(".team-shot")];
  const teamPin = document.querySelector(".team-pin");
  if (teamCards.length) {
    function paintTeam() {
      const active = nearestToCentre(teamCards);
      if (!active) return;
      const want = active.dataset.slide;
      teamShots.forEach((s) => s.classList.toggle("is-on", s.dataset.slide === want));
      teamCards.forEach((c) => c.classList.toggle("is-active", c === active));
      if (teamPin) teamPin.classList.toggle("is-faded", +want > 0);
    }
    addEventListener("scroll", paintTeam, { passive: true });
    addEventListener("resize", paintTeam, { passive: true });
    paintTeam();
  }

  /* ---- Pricing: Riders / Coaches audience toggle ---- */
  const planBtns = [...document.querySelectorAll(".pt-btn")];
  const planCards = [...document.querySelectorAll(".plan[data-aud]")];
  if (planBtns.length && planCards.length) {
    function paintPlans(aud) {
      planBtns.forEach((b) => {
        const on = b.dataset.aud === aud;
        b.classList.toggle("is-on", on);
        b.setAttribute("aria-selected", String(on));
      });
      planCards.forEach((c) => { c.hidden = c.dataset.aud !== aud; });
      // the cards that just appeared have never been revealed, so unhide them
      planCards.forEach((c) => {
        if (!c.hidden && c.classList.contains("rv")) c.classList.add("in");
      });
    }
    planBtns.forEach((b) => b.addEventListener("click", () => paintPlans(b.dataset.aud)));
    paintPlans("riders");
  }

  /* ---- 09 · Ride Recap: draw the trail, pop each milestone as the line reaches it ---- */
  const trail = document.getElementById("recap-trail");
  const trailPath = document.getElementById("trail-path");
  if (trail && trailPath) {
    const miles = [...trail.querySelectorAll(".mile")];
    const len = trailPath.getTotalLength();
    trailPath.style.strokeDasharray = len;
    trailPath.style.strokeDashoffset = len;
    function paintTrail() {
      const r = trail.getBoundingClientRect();
      const vh = window.innerHeight;
      // 0 when the trail's top hits 95% of the viewport, 1 once it has risen past 20%
      const p = clamp((vh * 0.95 - r.top) / (vh * 0.75), 0, 1);
      trailPath.style.strokeDashoffset = len * (1 - p);
      miles.forEach((m) => m.classList.toggle("is-on", p >= parseFloat(m.dataset.at)));
    }
    addEventListener("scroll", paintTrail, { passive: true });
    addEventListener("resize", paintTrail, { passive: true });
    paintTrail();
  }

  if (reduced || !hasGsap) {
    // static-but-complete experience: show the ride line fully, skip the parallax
    const rp = document.getElementById("ride-path");
    if (rp) { rp.style.strokeDasharray = "none"; rp.style.strokeDashoffset = "0"; }
    return;
  }

  gsap.registerPlugin(ScrollTrigger);

  /* ---- hero parallax layers ---- */
  gsap.to("#hero-video", {
    scale: 1.0, yPercent: 8, ease: "none",
    scrollTrigger: { trigger: ".hero", start: "top top", end: "bottom top", scrub: true },
  });
  gsap.to(".hero-type", {
    yPercent: -18, ease: "none",
    scrollTrigger: { trigger: ".hero", start: "top top", end: "bottom top", scrub: true },
  });

  /* ---- 04 · arena track draw-on ---- */
  /* ---- 04 · the GPS photo drifts as you pass it ---- */
  gsap.to(".map-bg img", {
    scale: 1.08, ease: "none",
    scrollTrigger: { trigger: ".map", start: "top bottom", end: "bottom top", scrub: true },
  });

  /* ---- 04 · the ride line draws itself as you scroll ----
     Restored 2 Sep at Bella's request, organic shape rather than the old arena
     rectangle. Guarded, and the parallax above sits outside the guard so removing
     the svg again cannot silently kill it. */
  const path = document.getElementById("ride-path");
  const dot = document.getElementById("ride-dot");
  if (path && dot) {
    const svg = path.ownerSVGElement;
    const isPhone = () => window.matchMedia("(max-width: 640px)").matches;

    /* Bella, comment 24: on a phone the line rendered 181px wide with its centre at x254
       against a screen centre of 188. The viewBox is 1000x700 but the path only fills
       x 435-919 of it, because the DESKTOP layout keeps the copy in the left half and the
       line in the right. A phone stacks the copy above and gives the line the whole row,
       so reframe the viewBox tightly around the path and let xMidYMid centre it. */
    const wideVB = svg.getAttribute("viewBox");
    function frameLine() {
      svg.setAttribute("viewBox", wideVB);          /* measure in the authored units */
      if (!isPhone()) return;
      const b = path.getBBox();
      /* 1.55 not 1.24: 1.24 put the line at 81% of the screen, which overshot Bella's
         "slightly bigger". 1.55 lands it at ~65%, up from the 48% it had, and keeps it
         in the same proportion band as desktop (48%). */
      const w = b.width * 1.55, h = b.height * 1.55;
      svg.setAttribute("viewBox", [b.x + b.width / 2 - w / 2,
                                   b.y + b.height / 2 - h / 2, w, h].join(" "));
    }
    frameLine();

    const len = path.getTotalLength();
    path.style.strokeDasharray = len;
    path.style.strokeDashoffset = len;

    /* The section-wide window finished the draw with .map's top still at 406px, i.e. before
       the svg (section y 505-897) had entered the viewport, so on a phone the line was
       always already complete and read as static. Drive it off the svg's own position
       there; desktop keeps the authored section window. */
    const win = isPhone()
      ? { trigger: svg, start: "top 90%", end: "bottom 45%", scrub: 0.6 }
      : { trigger: ".map", start: "top 70%", end: "80% bottom", scrub: 0.6 };

    gsap.to(path, { strokeDashoffset: 0, ease: "none", scrollTrigger: win });
    gsap.to(dot, {
      opacity: 1, ease: "none",
      scrollTrigger: Object.assign({}, win, {
        onUpdate: (self) => {
          const p = path.getPointAtLength(len * self.progress);
          dot.setAttribute("cx", p.x);
          dot.setAttribute("cy", p.y);
        },
      }),
    });

    addEventListener("resize", () => { frameLine(); ScrollTrigger.refresh(); }, { passive: true });
  }

  /* ---- device float depths ---- */
  document.querySelectorAll(".device").forEach((d, i) => {
    gsap.from(d, {
      y: 60 + i * 40, ease: "none",
      scrollTrigger: { trigger: ".devices", start: "top 90%", end: "top 30%", scrub: true },
    });
  });
})();

/* hero-video autoplay nudge: some contexts defer muted autoplay (background tabs,
   low-power mode). Retry on first scroll and whenever the tab becomes visible. */
(() => {
  const hv = document.getElementById("hero-video");
  if (!hv) return;
  const kick = () => { if (hv.paused) { const p = hv.play(); if (p && p.catch) p.catch(() => {}); } };
  document.addEventListener("visibilitychange", () => { if (!document.hidden) kick(); });
  addEventListener("scroll", kick, { once: true, passive: true });
  kick();
})();

/* ---- Pair it with a Pivo Pod: live prices (Kelly via Bella, 18 Sep) ----
   The markup carries the prices the store showed on 18 Sep. This swaps in the live ones from the store's
   /products/<handle>.js, which answers cross-origin, so the ivanbuilds preview and the store page show the
   same numbers the product card on the live page does: "From" when variants differ, the compare-at price
   struck through and a "Save" badge when it is higher. If the request fails the static prices stay. */
(function () {
  const cards = document.querySelectorAll(".pair-card[data-handle]");
  if (!cards.length || !window.fetch) return;
  const money = c => "$" + (c / 100).toFixed(2);
  const short = c => "$" + (c % 100 ? (c / 100).toFixed(2) : String(c / 100));
  cards.forEach(card => {
    fetch("https://pivoequestrian.com/products/" + card.dataset.handle + ".js", { credentials: "omit" })
      .then(r => (r.ok ? r.json() : Promise.reject(r.status)))
      .then(p => {
        const price = card.querySelector("[data-price]"), badge = card.querySelector("[data-save]");
        const sale = p.compare_at_price && p.compare_at_price > p.price;
        price.textContent = (p.price_varies ? "From " : "") + money(p.price);
        if (sale) { const s = document.createElement("s"); s.textContent = money(p.compare_at_price); price.append(" ", s); }
        if (badge) { badge.hidden = !sale; if (sale) badge.textContent = "Save " + short(p.compare_at_price - p.price); }
        if (!p.available) price.append(" · Sold out");
      })
      .catch(() => {});
  });
})();
